import React, { useEffect, useMemo, useRef, useState } from "react";
import ProfileHeader from "../profile-header/profile-header-and-breadcrumbs";
import "./shared-dashboard.scss";
import CheckIcon from "@mui/icons-material/Check";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { ReactComponent as ChartIcon } from "../../../assets/icons/chart-icon.svg";
import { ReactComponent as DashboardDollarIcon } from "../../../assets/icons/dashboard-dollar.svg";
import { ReactComponent as CalendarReportIcon } from "../../../assets/icons/calendar-report-icon.svg";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableSortLabel,
  TableRow,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import {
  exportPreneedDashboardSection,
  getPreneedDashboardData,
} from "../../../api/preneed-section/preneed-dashboard.api";
import {
  exportProviderDashboardSection,
  getProviderDashboardData,
} from "../../../api/provider-section/provider-dashboard.api";
import { getCurrentUserDetails } from "../../routines/user-operations";
import {
  BookingSummaryItem,
  DashboardPayloadLike,
  PROJECTED_EARNINGS_HEADERS,
  ProjectedRow,
  ProviderServiceSummary,
  ServiceChartItem,
  SharedDashboardProps,
  SharedDashboardTableColumnKey,
} from "./shared-dashboard.helper";
import { AlertComponent, SearchInputComponent } from "..";
import {
  formatCurrency,
  getBackendTimezone,
  getSessionToken,
} from "../../utils/dashboard-utils";
import { useSetLoading } from "../../hooks/appearance";

type SortDirection = "asc" | "desc";
type ExportCardType = "bookings" | "services" | "earnings";

const EMPTY_BOOKING_SUMMARY: BookingSummaryItem[] = [
  {
    label: "Completed",
    value: 0,
    tone: "success",
    icon: <CheckIcon sx={{ fontSize: 16 }} />,
  },
  {
    label: "New",
    value: 0,
    tone: "info",
    icon: <AccessTimeIcon sx={{ fontSize: 16 }} />,
  },
  {
    label: "Active",
    value: 0,
    tone: "warning",
    icon: <AccessTimeIcon sx={{ fontSize: 16 }} />,
  },
  {
    label: "Cancelled",
    value: 0,
    tone: "danger",
    icon: <CloseIcon sx={{ fontSize: 16 }} />,
  },
];

const SharedDashboard: React.FC<SharedDashboardProps> = ({
  mode = "provider",
}) => {
  const [showDateFilter, setShowDateFilter] = useState(false);
  const filterWrapRef = useRef<HTMLDivElement | null>(null);
  const [fromDate, setFromDate] = useState<Dayjs | null>(
    dayjs().startOf("month"),
  );
  const [toDate, setToDate] = useState<Dayjs | null>(dayjs().endOf("day"));
  const [isThisMonthSelected, setIsThisMonthSelected] = useState(true);

  const [bookingSummaryData, setBookingSummaryData] = useState<
    BookingSummaryItem[]
  >(EMPTY_BOOKING_SUMMARY);
  const [serviceData, setServiceData] = useState<ServiceChartItem[]>([]);
  const [projectedRowsData, setProjectedRowsData] = useState<ProjectedRow[]>(
    [],
  );
  const [bookingsTotal, setBookingsTotal] = useState(0);
  const [servicesTotal, setServicesTotal] = useState(0);
  const [earningsTotal, setEarningsTotal] = useState(0);
  const [exportingCard, setExportingCard] = useState<ExportCardType | null>(
    null,
  );
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<
    "error" | "success" | "info" | "warning"
  >("info");
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const fromDateTs = fromDate?.valueOf() ?? 0;
  const toDateTs = toDate?.valueOf() ?? 0;

  const serviceTitle = mode === "preNeed" ? "My Packages" : "My Services";

  const PACKAGE_ORDER = ["Basic", "Standard", "Premium"];
  const packageSortIndex = (label: string) => {
    const idx = PACKAGE_ORDER.findIndex(
      (p) => p.toLowerCase() === (label || "").trim().toLowerCase(),
    );
    return idx >= 0 ? idx : PACKAGE_ORDER.length;
  };

  const serviceChartMax = useMemo(() => {
    const highest = Math.max(
      0,
      ...serviceData.map((entry) => Math.max(entry.completed, entry.cancelled)),
    );
    if (highest <= 0) return 60;
    return Math.ceil(highest * 1.2);
  }, [serviceData]);

  const serviceChartOptions: ApexOptions = {
    chart: {
      id: `service-breakdown-chart-${mode}`,
      toolbar: { show: false },
      fontFamily: "Inter, Roboto, sans-serif",
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "42%",
        borderRadius: 4,
      },
    },
    dataLabels: { enabled: false },
    stroke: { show: false },
    xaxis: {
      categories: serviceData.map((entry) => entry.label),
      labels: {
        trim: true,
        style: {
          colors: serviceData.map(() => "#6B7280"),
          fontSize: "12px",
          fontWeight: 400,
        },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      max: serviceChartMax,
      tickAmount: 2,
      labels: {
        style: {
          colors: ["#9AA2AF"],
          fontSize: "12px",
          fontWeight: 400,
        },
      },
    },
    grid: {
      borderColor: "#E4E7EC",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
      padding: { top: 0, right: 8, bottom: 0, left: 6 },
    },
    colors: ["#104145", "#EBD28D"],
    legend: {
      show: true,
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "14px",
      markers: { width: 10, height: 10, radius: 10, offsetY: 1 },
      labels: { colors: "#526079" },
      itemMargin: { horizontal: 14 },
    },
    tooltip: { enabled: true },
  };

  const serviceChartSeries = [
    {
      name: "Completed",
      data: serviceData.map((entry) => entry.completed),
    },
    {
      name: "Cancelled",
      data: serviceData.map((entry) => entry.cancelled),
    },
  ];

  const servicesChartRef = useRef<HTMLDivElement | null>(null);
  const [servicesChartViewportWidth, setServicesChartViewportWidth] =
    useState(0);

  useEffect(() => {
    const chartContainer = servicesChartRef.current;
    if (!chartContainer) return;

    const updateViewportWidth = () => {
      setServicesChartViewportWidth(chartContainer.clientWidth);
    };

    updateViewportWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateViewportWidth);
      return () => window.removeEventListener("resize", updateViewportWidth);
    }

    const resizeObserver = new ResizeObserver(updateViewportWidth);
    resizeObserver.observe(chartContainer);

    return () => resizeObserver.disconnect();
  }, []);

  const servicesChartWidth = useMemo(() => {
    if (!servicesChartViewportWidth) return "100%";
    const requiredChartWidth = serviceData.length * 120;
    return `${Math.max(servicesChartViewportWidth, requiredChartWidth)}px`;
  }, [serviceData.length, servicesChartViewportWidth]);

  const isServicesChartScrollable = useMemo(() => {
    if (!servicesChartViewportWidth) return false;
    const requiredChartWidth = serviceData.length * 120;
    return requiredChartWidth > servicesChartViewportWidth + 4;
  }, [serviceData.length, servicesChartViewportWidth]);

  const [sortConfig, setSortConfig] = useState<{
    key: SharedDashboardTableColumnKey | null;
    direction: SortDirection;
  }>({ key: null, direction: "asc" });

  const parseValueForSort = (
    row: ProjectedRow,
    key: SharedDashboardTableColumnKey,
  ) => {
    if (key === "date") return new Date(row.date).getTime() || 0;
    if (
      key === "myEarnings" ||
      key === "afpCommission" ||
      key === "orderAmount"
    ) {
      return Number(row[key].replace(/[^0-9.-]+/g, "")) || 0;
    }
    return row[key].toString().toLowerCase();
  };

  const sortedProjectedRows = useMemo(() => {
    if (!sortConfig.key) return projectedRowsData;

    return [...projectedRowsData].sort((a, b) => {
      const valueA = parseValueForSort(
        a,
        sortConfig.key as SharedDashboardTableColumnKey,
      );
      const valueB = parseValueForSort(
        b,
        sortConfig.key as SharedDashboardTableColumnKey,
      );

      if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [projectedRowsData, sortConfig]);

  const handleSort = (key: SharedDashboardTableColumnKey) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" },
    );
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const clickedInsideDatePicker =
        !!target.closest(".MuiPickersPopper-root") ||
        !!target.closest(".MuiPickersLayout-root") ||
        !!target.closest(".MuiPickersDay-root") ||
        !!target.closest(".MuiDateCalendar-root") ||
        !!target.closest(".MuiDialog-root");

      if (clickedInsideDatePicker) {
        return;
      }

      if (filterWrapRef.current && !filterWrapRef.current.contains(target)) {
        setShowDateFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const queryFrom = (fromDate ?? dayjs().startOf("month")).startOf("day");
    const queryTo = (toDate ?? dayjs()).endOf("day");
    const sortParams = sortConfig.key
      ? { sortBy: sortConfig.key, sortDirection: sortConfig.direction }
      : {};

    const applyDashboardPayload = (dashboardData: DashboardPayloadLike) => {
      const bookingMap = (dashboardData.myBookings || []).reduce(
        (acc, item) => {
          acc[item.status?.toLowerCase()] = Number(item.count || 0);
          return acc;
        },
        {} as Record<string, number>,
      );

      setBookingSummaryData(
        EMPTY_BOOKING_SUMMARY.map((entry) => {
          if (entry.label === "Completed")
            return { ...entry, value: bookingMap.completed || 0 };
          if (entry.label === "New")
            return { ...entry, value: bookingMap.new || 0 };
          if (entry.label === "Active")
            return { ...entry, value: bookingMap.active || 0 };
          if (entry.label === "Cancelled")
            return { ...entry, value: bookingMap.cancelled || 0 };
          return entry;
        }),
      );
      setBookingsTotal(Number(dashboardData.myBookingsTotal));

      const sourceItems = (dashboardData.myPackages ||
        dashboardData.myServices ||
        []) as ProviderServiceSummary[];
      const mappedServices: ServiceChartItem[] = sourceItems.map((item) => ({
        label: item.packageName || item.categoryName || "Unknown",
        completed: Number(item.completed || 0),
        cancelled: Number(item.cancelled || 0),
      }));
      const sortedServices =
        mode === "preNeed"
          ? [...mappedServices].sort(
              (a, b) => packageSortIndex(a.label) - packageSortIndex(b.label),
            )
          : mappedServices;
      setServiceData(sortedServices);
      const servicesTotalFromApi =
        mode === "preNeed"
          ? dashboardData.myPackagesTotal
          : dashboardData.myServicesTotal;
      setServicesTotal(Number(servicesTotalFromApi));

      const mappedEarnings: ProjectedRow[] = (
        dashboardData.myEarnings || []
      ).map((item) => ({
        date: item.orderDate
          ? dayjs(item.orderDate).format("MMM DD, YYYY")
          : "-",
        orderId: item.orderId || "-",
        orderAmount: formatCurrency(item.orderAmount),
        afpCommission: formatCurrency(item.afpCommission),
        myEarnings: formatCurrency(item.myEarnings),
      }));
      setProjectedRowsData(mappedEarnings);
      setEarningsTotal(Number(dashboardData.myEarningsTotal));
    };

    const fetchDashboardByMode = async () => {
      setIsLoading(true);
      if (mode === "preNeed") {
        const preNeedSession = getCurrentUserDetails("preNeed");
        const token = getSessionToken(preNeedSession);
        if (!token) {
          setIsLoading(false);
          return;
        }

        try {
          const timezone = getBackendTimezone();
          const from = queryFrom.format("YYYY-MM-DD HH:mm:ss");
          const to = queryTo.format("YYYY-MM-DD HH:mm:ss.SSS");

          const response = await getPreneedDashboardData(
            {
              timezone,
              startDate: from,
              endDate: to,
              ...(searchText ? { searchText } : {}),
              ...sortParams,
            },
            token,
          );
          const dashboardData: DashboardPayloadLike | undefined =
            response?.data?.data?.data || response?.data?.data;
          if (dashboardData) applyDashboardPayload(dashboardData);
        } catch (error) {
          console.error("Failed to fetch preneed dashboard data:", error);
        } finally {
          setIsLoading(false);
        }
        return;
      }

      setBookingSummaryData(EMPTY_BOOKING_SUMMARY);
      setServiceData([]);
      setProjectedRowsData([]);
      setBookingsTotal(0);
      setServicesTotal(0);
      setEarningsTotal(0);

      try {
        const providerSession = getCurrentUserDetails("provider");
        const token = getSessionToken(providerSession);
        if (!token) {
          setIsLoading(false);
          return;
        }

        const timezone = getBackendTimezone();
        const from = queryFrom.format("YYYY-MM-DD HH:mm:ss");
        const to = queryTo.format("YYYY-MM-DD HH:mm:ss.SSS");

        const response = await getProviderDashboardData(
          {
            timezone,
            startDate: from,
            endDate: to,
            ...(searchText ? { searchText } : {}),
            ...sortParams,
          },
          token,
        );
        if (response?.data?.success) {
          const dashboardData =
            response?.data?.data?.data || response?.data?.data;
          applyDashboardPayload(dashboardData);
        }
      } catch (error) {
        console.error("Failed to fetch provider dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardByMode();
  }, [
    mode,
    fromDateTs,
    toDateTs,
    sortConfig.key,
    sortConfig.direction,
    searchText,
  ]);

  const rangeLabel = useMemo(() => {
    if (isThisMonthSelected) {
      return "This Month";
    }
    if (fromDate && toDate) {
      return `${fromDate.format("DD MMM")} - ${toDate.format("DD MMM")}`;
    }
    if (fromDate) {
      return `${fromDate.format("DD MMM")} -`;
    }
    return "This Month";
  }, [fromDate, toDate, isThisMonthSelected]);
  const canExportBookings = bookingsTotal > 0;
  const canExportServices = servicesTotal > 0;
  const canExportEarnings = projectedRowsData.length > 0 || earningsTotal > 0;

  const handleResetThisMonth = () => {
    setFromDate(dayjs().startOf("month"));
    setToDate(dayjs().endOf("day"));
    setIsThisMonthSelected(true);
    setShowDateFilter(false);
  };

  const handleFromDateChange = (newValue: Dayjs | null) => {
    if (!newValue || !newValue.isValid()) return;
    setIsThisMonthSelected(false);
    setFromDate(newValue.startOf("day"));
  };

  const handleToDateChange = (newValue: Dayjs | null) => {
    if (!newValue || !newValue.isValid()) return;
    setIsThisMonthSelected(false);
    setToDate(newValue.endOf("day"));
  };

  const handleExport = async (card: ExportCardType) => {
    if (exportingCard) return;

    const timezone = getBackendTimezone();
    const queryFrom = (fromDate ?? dayjs().startOf("month")).startOf("day");
    const queryTo = (toDate ?? dayjs()).endOf("day");
    const from = queryFrom.format("YYYY-MM-DD HH:mm:ss");
    const to = queryTo.format("YYYY-MM-DD HH:mm:ss");
    const sortParams = sortConfig.key
      ? { sortBy: sortConfig.key, sortDirection: sortConfig.direction }
      : {};

    setIsLoading(true);

    try {
      setExportingCard(card);
      const session = getCurrentUserDetails(
        mode === "preNeed" ? "preNeed" : "provider",
      );
      const token = getSessionToken(session);
      if (!token) return;

      let response;

      if (mode === "preNeed") {
        const section =
          card === "bookings"
            ? "plannerMyBookings"
            : card === "services"
              ? "plannerMyPackages"
              : "plannerMyEarnings";
        response = await exportPreneedDashboardSection(
          {
            section,
            timezone,
            startDate: from,
            endDate: to,
            ...(searchText ? { searchText } : {}),
            ...sortParams,
          },
          token,
        );
      } else {
        const section =
          card === "bookings"
            ? "providerMyBookings"
            : card === "services"
              ? "providerMyServices"
              : "providerMyEarnings";

        response = await exportProviderDashboardSection(
          {
            section,
            timezone,
            startDate: from,
            endDate: to,
            ...(searchText ? { searchText } : {}),
            ...sortParams,
          },
          token,
        );
      }
      const message = response?.data?.message || response?.data?.data?.message;
      setAlertMessage(`${message} Please check your email.`);
      setAlertType("success");
      setAlertOpen(true);
    } catch (error) {
      console.error(`Failed to export ${card}:`, error);
      const err = error as { response?: { data?: { message?: string } } };
      setAlertMessage(
        err?.response?.data?.message || `Failed to generate ${card} report`,
      );
      setAlertType("error");
      setAlertOpen(true);
    } finally {
      setExportingCard(null);
      setIsLoading(false);
    }
  };

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <div className="shared-dashboard-main-wrapper">
      <ProfileHeader mode={mode} />

      <div className="shared-dashboard-content-wrapper">
        <div className="shared-dashboard-heading-row">
          <h2 className="shared-dashboard-welcome">
            Welcome to your dashboard
          </h2>
          <div className="service-filter-wrap" ref={filterWrapRef}>
            <button
              type="button"
              className="month-filter-button"
              onClick={() => setShowDateFilter((prev) => !prev)}
            >
              <CalendarReportIcon />
              {rangeLabel}
              <KeyboardArrowDownIcon fontSize="small" />
            </button>

            {showDateFilter && (
              <div className="date-filter-popover">
                <button
                  type="button"
                  className={`${isThisMonthSelected ? "active-range-chip selected" : "active-range-chip"}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleResetThisMonth();
                  }}
                >
                  This Month
                </button>
                <div
                  className={`${isThisMonthSelected ? "custom-date-title" : "custom-date-title selected"}`}
                >
                  Custom Date Range
                </div>
                <div className="custom-date-divider" />
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <label>
                    Start Date
                    <DesktopDatePicker
                      value={fromDate}
                      onChange={handleFromDateChange}
                      onAccept={handleFromDateChange}
                      format="DD MMM YYYY"
                      maxDate={toDate ?? dayjs()}
                      disableFuture
                      slots={{ openPickerIcon: CalendarReportIcon }}
                      slotProps={{
                        openPickerButton: {
                          sx: { color: "#36415380" },
                        },
                        textField: {
                          placeholder: "Enter date",
                          size: "small",
                          className: "date-picker-field",
                          fullWidth: true,
                        },
                      }}
                    />
                  </label>
                  <label>
                    End Date
                    <DesktopDatePicker
                      value={toDate}
                      onChange={handleToDateChange}
                      onAccept={handleToDateChange}
                      format="DD MMM YYYY"
                      minDate={fromDate ?? undefined}
                      maxDate={dayjs()}
                      disableFuture
                      slots={{ openPickerIcon: CalendarReportIcon }}
                      slotProps={{
                        openPickerButton: {
                          sx: { color: "#36415380" },
                        },
                        textField: {
                          placeholder: "Enter date",
                          size: "small",
                          className: "date-picker-field",
                          fullWidth: true,
                        },
                      }}
                    />
                  </label>
                </LocalizationProvider>
              </div>
            )}
          </div>
        </div>

        <div className="shared-dashboard-top-grid">
          <section className="shared-dashboard-card bookings-card">
            <div className="card-head">
              <div className="bookings-container">
                <div className="card-title-wrap">
                  <CalendarReportIcon />
                  <span className="card-title">My Orders</span>
                </div>
                <div className="big-total">{bookingsTotal}</div>
              </div>
              <button
                type="button"
                className="export-button"
                onClick={() => handleExport("bookings")}
                disabled={exportingCard === "bookings" || !canExportBookings}
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                Export
              </button>
            </div>

            <div className="booking-status-grid">
              {bookingSummaryData.map((entry) => (
                <article
                  key={entry.label}
                  className={`status-tile status-tile--${entry.tone}`}
                >
                  <div
                    className={`status-icon-ring status-icon-ring--${entry.tone}`}
                  >
                    <div className={`status-icon status-icon--${entry.tone}`}>
                      {entry.icon}
                    </div>
                  </div>
                  <div className="status-details">
                    <span className={`status-label--${entry.tone}`}>
                      {entry.label}
                    </span>
                    <strong className={`status-value--${entry.tone}`}>
                      {entry.value}
                    </strong>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="shared-dashboard-card services-card">
            <div className="card-head">
              <div className="card-title-wrap">
                <ChartIcon className="title-icon" />
                <span className="card-title">{serviceTitle}</span>
              </div>
              <button
                type="button"
                className="export-button"
                onClick={() => handleExport("services")}
                disabled={exportingCard === "services" || !canExportServices}
              >
                <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
                Export
              </button>
            </div>

            <div className="big-total">{servicesTotal}</div>

            <div
              className={`services-chart ${isServicesChartScrollable ? "services-chart--scrollable" : ""}`}
              ref={servicesChartRef}
            >
              <div
                className="services-chart-inner"
                style={{ width: servicesChartWidth }}
              >
                <Chart
                  options={serviceChartOptions}
                  series={serviceChartSeries}
                  type="bar"
                  height={210}
                />
              </div>
            </div>
          </section>
        </div>

        <section className="shared-dashboard-card projected-earnings-card">
          <div className="earnings-head">
            <div className="earnings-title-wrap">
              <div className="earnings-title-row">
                <DashboardDollarIcon />
                <div className="table-header">My Projected Earnings</div>
              </div>
              <p className="earnings-subtext">
                Detailed breakdown of earnings from confirmed and completed services
              </p>
            </div>
            <div className="earnings-total-wrap">
              <div className="earnings-total">
                Total: {formatCurrency(earningsTotal)}
              </div>
              <button
                type="button"
                className="export-button"
                onClick={() => handleExport("earnings")}
                disabled={exportingCard === "earnings" || !canExportEarnings}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
                Export
              </button>
            </div>
          </div>

          <div className="search-row">
            <SearchInputComponent
              placeholder={`Search by Order ID`}
              onSearch={(value: string) => setSearchText(value)}
            />
          </div>

          <TableContainer
            component={Paper}
            elevation={0}
            className="projected-table-wrap"
          >
            <Table>
              <TableHead>
                <TableRow>
                  {PROJECTED_EARNINGS_HEADERS.map((header) => (
                    <TableCell key={header.key} component="th">
                      <TableSortLabel
                        active={sortConfig.key === header.key}
                        direction={
                          sortConfig.key === header.key
                            ? sortConfig.direction
                            : "asc"
                        }
                        onClick={() => handleSort(header.key)}
                      >
                        {header.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedProjectedRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      align="center"
                      style={{ fontStyle: "italic", color: "#888" }}
                    >
                      No data available
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedProjectedRows.map((row, index) => (
                    <TableRow key={`${row.orderId}-${row.date}-${index}`}>
                      <TableCell>{row.date}</TableCell>
                      <TableCell>{row.orderId}</TableCell>
                      <TableCell>{row.orderAmount}</TableCell>
                      <TableCell>{row.afpCommission}</TableCell>
                      <TableCell className="amount-cell">
                        {row.myEarnings}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </section>
      </div>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
      />
    </div>
  );
};

export default SharedDashboard;
